import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { eventsAPI } from '../api';

/**
 * Month-grid calendar showing event dots and a tooltip/list under the grid for
 * the currently selected day. Keeps things lightweight (no external library).
 */
const monthLabel = (y, m) =>
  new Date(y, m, 1).toLocaleString('en-GB', { month: 'long', year: 'numeric' });

const EventsCalendar = ({ localOnly = false }) => {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth()); // 0-11
  const [events, setEvents] = useState([]);
  const [selectedDay, setSelectedDay] = useState(today.getDate());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const yyyymm = `${year}-${String(month + 1).padStart(2, '0')}`;
    // Paisley and Glasgow together run 400+ events a month. The old single
    // request for 200 only reached the 18th, so later dates showed no dots and
    // claimed "no events" when there were plenty. Page until the month is in.
    const loadMonth = async () => {
      const PAGE = 500;
      const all = [];
      for (let skip = 0; skip < 2000; skip += PAGE) {
        const res = await eventsAPI.list({ month: yyyymm, limit: PAGE, skip, local_only: localOnly });
        const rows = res.data || [];
        all.push(...rows);
        if (rows.length < PAGE) break;
      }
      return all;
    };

    loadMonth()
      .then((items) => {
        setEvents(items);
        // Auto-select first day with events so the chips render immediately.
        // If today is in the current view, prefer it when it has events.
        const todayInView =
          year === today.getFullYear() && month === today.getMonth() ? today.getDate() : null;
        const daysWithEvents = [...new Set(items.map((ev) => new Date(ev.start_date).getDate()))]
          .sort((a, b) => a - b);
        // Prefer today, then the next day that has something on — never open
        // on a date that has already passed.
        const nextWithEvents = todayInView
          ? daysWithEvents.find((d) => d >= todayInView)
          : daysWithEvents[0];
        if (todayInView && daysWithEvents.includes(todayInView)) {
          setSelectedDay(todayInView);
        } else if (nextWithEvents) {
          setSelectedDay(nextWithEvents);
        } else if (todayInView) {
          setSelectedDay(todayInView);
        }
      })
      .catch(() => setEvents([]))
      .finally(() => setLoading(false));
  }, [year, month, localOnly]); // eslint-disable-line react-hooks/exhaustive-deps

  // Group events by day-of-month
  const eventsByDay = useMemo(() => {
    const map = {};
    for (const ev of events) {
      const d = new Date(ev.start_date);
      if (d.getFullYear() === year && d.getMonth() === month) {
        const day = d.getDate();
        (map[day] = map[day] || []).push(ev);
      }
    }
    return map;
  }, [events, year, month]);

  // Build the grid: 6 weeks × 7 days, ISO Monday-first
  const grid = useMemo(() => {
    const firstOfMonth = new Date(year, month, 1);
    const lastOfMonth = new Date(year, month + 1, 0);
    const startWeekday = (firstOfMonth.getDay() + 6) % 7; // Mon=0…Sun=6
    const totalDays = lastOfMonth.getDate();
    const cells = [];
    for (let i = 0; i < startWeekday; i++) cells.push(null);
    for (let d = 1; d <= totalDays; d++) cells.push(d);
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [year, month]);

  const goto = (deltaMonths) => {
    const d = new Date(year, month + deltaMonths, 1);
    setYear(d.getFullYear());
    setMonth(d.getMonth());
    setSelectedDay(null);
  };

  const isToday = (day) =>
    day &&
    year === today.getFullYear() &&
    month === today.getMonth() &&
    day === today.getDate();

  const selectedEvents = selectedDay ? eventsByDay[selectedDay] || [] : [];

  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden" data-testid="events-calendar">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 bg-gradient-to-r from-slate-50 to-cyan-50/40">
        <button
          type="button"
          onClick={() => goto(-1)}
          className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-white border border-transparent hover:border-gray-200 transition-all"
          aria-label="Previous month"
          data-testid="calendar-prev-btn"
        >
          <ChevronLeft size={18} />
        </button>
        <h3 className="text-lg font-semibold text-slate-900 tracking-tight" data-testid="calendar-month-label">
          {monthLabel(year, month)}
        </h3>
        <button
          type="button"
          onClick={() => goto(1)}
          className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-white border border-transparent hover:border-gray-200 transition-all"
          aria-label="Next month"
          data-testid="calendar-next-btn"
        >
          <ChevronRight size={18} />
        </button>
      </div>

      {/* Weekday header */}
      <div className="grid grid-cols-7 px-3 pt-3 text-[11px] uppercase tracking-wider text-gray-400 font-semibold">
        {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((d) => (
          <div key={d} className="text-center py-1">{d}</div>
        ))}
      </div>

      {/* Day grid */}
      <div className="grid grid-cols-7 gap-1 px-3 pb-3" data-testid="calendar-grid">
        {grid.map((day, idx) => {
          if (day === null) {
            return <div key={`pad-${idx}`} className="aspect-square" />;
          }
          const dayEvents = eventsByDay[day] || [];
          const hasEvents = dayEvents.length > 0;
          const featured = dayEvents.some((e) => e.featured);
          const selected = day === selectedDay;
          return (
            <button
              key={`d-${day}`}
              type="button"
              onClick={() => setSelectedDay(day)}
              className={`aspect-square rounded-lg flex flex-col items-center justify-center text-sm transition-all relative
                ${selected
                  ? 'bg-cyan-500 text-white shadow-md shadow-cyan-500/40'
                  : isToday(day)
                  ? 'bg-cyan-50 text-cyan-700 ring-1 ring-cyan-200'
                  : hasEvents
                  ? 'bg-slate-50 text-slate-900 hover:bg-cyan-50'
                  : 'text-gray-400 hover:bg-slate-50'}`}
              data-testid={`calendar-day-${day}`}
            >
              <span className="font-medium leading-none">{day}</span>
              {hasEvents && (
                <span className="flex gap-0.5 mt-1">
                  {dayEvents.slice(0, 3).map((_, i) => (
                    <span
                      key={i}
                      className={`w-1 h-1 rounded-full ${selected ? 'bg-white' : featured ? 'bg-fuchsia-500' : 'bg-cyan-500'}`}
                    />
                  ))}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Selected day events */}
      <div className="border-t border-gray-100 px-5 py-4 bg-slate-50/50 min-h-[100px]" data-testid="calendar-selected-events">
        {loading ? (
          <p className="text-xs text-gray-400">Loading events…</p>
        ) : !selectedDay ? (
          <p className="text-sm text-gray-500">Click a day to see events</p>
        ) : selectedEvents.length === 0 ? (
          <p className="text-sm text-gray-500">
            No events on {new Date(year, month, selectedDay).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}.
          </p>
        ) : (
          <>
            <p className="text-xs uppercase tracking-wide text-gray-500 mb-3 font-semibold">
              {new Date(year, month, selectedDay).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}
              <span className="ml-2 text-cyan-600">· {selectedEvents.length} event{selectedEvents.length === 1 ? '' : 's'}</span>
            </p>
            <ul className="space-y-2">
              {selectedEvents.map((ev) => (
                <li key={ev.id}>
                  <Link
                    to={`/events/${ev.slug}`}
                    className="block group hover:bg-white rounded-lg px-3 py-2 -mx-3 transition-colors"
                    data-testid={`calendar-event-${ev.slug}`}
                  >
                    <p className="text-sm font-medium text-slate-900 group-hover:text-cyan-600">
                      {ev.featured && <span className="text-fuchsia-500 mr-1">★</span>}
                      {ev.title}
                    </p>
                    <p className="text-xs text-gray-500">
                      {new Date(ev.start_date).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                      {ev.venue && ` · ${ev.venue}`}
                    </p>
                  </Link>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </div>
  );
};

export default EventsCalendar;
